import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trophy } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { PageHeader } from "../components/common";
import { Avatar, Staff } from "../components/StaffAvatar";

const CUP = ["#d4a017", "#9aa3ad", "#b87333"];
const lakh = (v) => `₹${(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })} L`;
const pos = (n) => `${n} PO${n === 1 ? "" : "s"}`;

const Pills = ({ value, onChange, opts, tid }) => (
  <div className="inline-flex border rounded-full p-0.5" data-testid={tid}>
    {opts.map(([v, l]) => (
      <button key={v} data-testid={`${tid}-${v}`} onClick={() => onChange(v)}
        className={`h-8 px-4 text-xs rounded-full transition-colors ${value === v ? "bg-primary text-primary-foreground" : "hover:text-primary"}`}>{l}</button>))}
  </div>
);

const Podium = ({ rows, metric, sub, score, tid }) => (
  <div className="grid grid-cols-3 gap-2 sm:gap-3 items-end mb-4">
    {[1, 0, 2].map((i) => (!rows[i] ? <div key={i} /> : (
      <div key={i} className={`ax-panel p-3 sm:p-4 text-center ax-fade ${i === 0 ? "pb-6 sm:pb-8 border-primary" : ""}`} data-testid={`${tid}-podium-${i + 1}`}>
        {score(rows[i]) > 0 ? <Trophy className={`${i === 0 ? "h-7 w-7" : "h-5 w-5"} mx-auto mb-2`} style={{ color: CUP[i] }} data-testid={`${tid}-trophy-${i + 1}`} />
          : <div className="text-xs text-muted-foreground mb-2">#{i + 1}</div>}
        <div className="flex justify-center"><Avatar name={rows[i].staff_name} size={i === 0 ? 64 : 48} /></div>
        <div className="font-semibold mt-2 truncate text-sm">{rows[i].staff_name}</div>
        <div className="num text-sm text-primary">{metric(rows[i])}</div>
        <div className="text-[11px] text-muted-foreground truncate">{sub(rows[i])}</div>
      </div>)))}
  </div>
);

const Board = ({ title, rows, metric, sub, score, tid, empty }) => (
  <section className="mb-8" data-testid={tid}>
    <h2 className="text-base md:text-lg font-semibold mb-3">{title}</h2>
    {!rows.length ? <p className="text-sm text-muted-foreground" data-testid={`${tid}-empty`}>{empty}</p> : <>
      <Podium rows={rows} metric={metric} sub={sub} score={score} tid={tid} />
      <div className="ax-panel ax-scroll ax-freeze"><table className="ax-table w-full">
        <thead><tr><th>Rank</th><th>Name</th><th>Score</th><th>Detail</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={r.staff_name} data-testid={`${tid}-row-${r.staff_name}`}>
            <td className="num">{score(r) > 0 && i < 3 ? <Trophy className="h-4 w-4 inline" style={{ color: CUP[i] }} /> : i + 1}</td>
            <td><Staff name={r.staff_name} size={22} /></td><td className="num">{metric(r)}</td><td className="text-xs text-muted-foreground">{sub(r)}</td></tr>))}
        </tbody></table></div>
    </>}
  </section>
);

export default function Leaderboard() {
  const [period, setPeriod] = useState("fy");
  const [tab, setTab] = useState("new");
  const [by, setBy] = useState("value");
  const [d, setD] = useState(null);
  const [locked, setLocked] = useState(false);
  useEffect(() => {
    setD(null);
    api.get("/sales/leaderboard", { params: { period } }).then((r) => setD(r.data)).catch((e) => (e.response?.status === 403 ? setLocked(true) : toast.error(errMsg(e))));
  }, [period]);
  if (locked) return <div className="ax-panel p-6 text-sm max-w-xl" data-testid="leaderboard-locked">The leaderboard is open to the sales team, branch heads and founders.</div>;
  const byValue = d?.show_value && by === "value";
  const rank = (rows) => (byValue ? rows : [...rows].sort((a, b) => b.count - a.count || (b.value ?? 0) - (a.value ?? 0)));
  const orders = { metric: (r) => (byValue ? lakh(r.value) : pos(r.count)), sub: (r) => (byValue ? pos(r.count) : d?.show_value ? lakh(r.value) : ""), score: (r) => r.count };
  const inside = { metric: (r) => `${r.calls + r.meetings}`, sub: (r) => `${r.calls} calls · ${r.meetings} meetings`, score: (r) => r.calls + r.meetings };
  return (
    <div className="max-w-5xl">
      <PageHeader eyebrow="Sales & Leads" title="Leaderboard" desc={`Who's leading ${d?.label || ""}. Business development and repeat business are ranked on POs won in the UID tracker; inside sales on calls and meetings logged in the app.`} />
      <div className="flex flex-wrap gap-3 mb-6">
        <Pills tid="board-tab" value={tab} onChange={setTab} opts={[["new", "New business"], ["repeat", "Repeat business"]]} />
        <Pills tid="board-period" value={period} onChange={setPeriod} opts={[["fy", "This financial year"], ["month", "This month"]]} />
        {d?.show_value && <Pills tid="board-by" value={by} onChange={setBy} opts={[["value", "PO value"], ["count", "Number of POs"]]} />}
      </div>
      {!d ? <p className="text-sm text-muted-foreground">Loading…</p> : tab === "new" ? <>
        <Board title="Business development · POs won" rows={rank(d.new.orders)} {...orders} tid="board-nbd" empty="No new-business POs in the UID tracker for this period yet." />
        <Board title="Inside sales · calls & meetings" rows={d.new.inside} {...inside} tid="board-inside" empty="No one with the inside sales role has a login yet." />
      </> : <Board title="Repeat business · POs won" rows={rank(d.repeat.orders)} {...orders} tid="board-repeat" empty="No repeat-business POs in the UID tracker for this period yet." />}
    </div>
  );
}
